import type { Component } from '../router';
import { ApiError } from '../services/api';
import { postContact, type ContactRequest } from '../api/client';
import { t } from '../i18n/locale';

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// errorCopy maps a failed submission to user-facing text. A 400 is a validation
// failure (the client already validates, so this is a fallback), 429 is the
// per-IP rate limit, anything else is a server/transport problem.
export function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return t('contact.error.badRequest');
    if (err.status === 429) return t('contact.error.rateLimited');
    return t('contact.error.server');
  }
  return t('contact.error.network');
}

// ContactPage is the public marketing contact form. It POSTs JSON to
// /api/contact through the shared transport (which attaches the CSRF header),
// mirroring the contract enforced server-side. No auth required.
export class ContactPage implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = el('section', 'contact');
    const card = el('div', 'contact__card');

    const intro = el('div', 'contact__intro');
    intro.append(
      el('h1', 'contact__title', t('contact.title')),
      el('p', 'contact__sub', t('contact.sub')),
    );

    card.append(intro, this.buildForm());
    section.appendChild(card);
    host.appendChild(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  private buildForm(): HTMLElement {
    const form = el('form', 'contact__form');
    form.noValidate = true;

    const name = field('text', 'name', t('contact.field.name'), {
      required: true,
      autocomplete: 'name',
      maxLength: 120,
    });
    const email = field('email', 'email', t('contact.field.email'), {
      required: true,
      autocomplete: 'email',
      maxLength: 254,
      placeholder: 'name@example.com',
    });
    const phone = field('tel', 'phone', t('contact.field.phone'), { autocomplete: 'tel', maxLength: 40 });
    const subject = field('text', 'subject', t('contact.field.subject'), { required: true, maxLength: 160 });
    const message = textareaField('message', t('contact.field.message'), { required: true, maxLength: 4000 });

    // Honeypot: visually hidden, off the tab order, never autofilled. A bot that
    // fills it is silently dropped server-side.
    const honeypot = el('div', 'contact__hp');
    honeypot.setAttribute('aria-hidden', 'true');
    const hpInput = el('input');
    hpInput.type = 'text';
    hpInput.name = 'company';
    hpInput.tabIndex = -1;
    hpInput.autocomplete = 'off';
    const hpLabel = el('label', undefined, t('contact.field.company'));
    hpLabel.htmlFor = 'contact-company';
    hpInput.id = 'contact-company';
    honeypot.append(hpLabel, hpInput);

    // Consent checkbox.
    const consentWrap = el('label', 'contact__consent');
    const consent = el('input', 'contact__consent-box');
    consent.type = 'checkbox';
    consent.name = 'consent';
    consent.required = true;
    const consentText = el('span', 'contact__consent-text', t('contact.consent'));
    consentText.append(' ');
    const consentLink = el('a', 'contact__consent-link', t('contact.consentLink'));
    consentLink.href = '/privacy';
    consentText.appendChild(consentLink);
    consentWrap.append(consent, consentText);

    const submit = el('button', 'contact__submit');
    submit.type = 'submit';
    submit.dataset.state = 'idle';
    submit.appendChild(el('span', 'contact__submit-label', t('contact.submit')));

    const status = el('p', 'contact__status');
    status.dataset.visible = 'false';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const setState = (next: SubmitState, message?: string, tone?: 'success' | 'error'): void => {
      submit.dataset.state = next;
      submit.disabled = next === 'sending';
      if (message) {
        status.textContent = message;
        status.dataset.visible = 'true';
        if (tone) status.dataset.tone = tone;
        else delete status.dataset.tone;
      } else {
        status.dataset.visible = 'false';
      }
    };

    form.addEventListener('input', () => {
      if (submit.dataset.state === 'error') setState('idle');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (submit.disabled) return;

      if (!form.checkValidity()) {
        setState('error', t('contact.validation'), 'error');
        form.reportValidity();
        return;
      }

      const body: ContactRequest = {
        name: name.input.value.trim(),
        email: email.input.value.trim(),
        phone: phone.input.value.trim() || undefined,
        subject: subject.input.value.trim(),
        message: message.input.value.trim(),
        company: hpInput.value.trim() || undefined,
        consent: consent.checked,
      };

      setState('sending', t('contact.sending'));
      try {
        await postContact(body);
        this.showConfirmation(form.parentElement as HTMLElement);
      } catch (err) {
        setState('error', errorCopy(err), 'error');
      }
    });

    form.append(
      name.field,
      email.field,
      phone.field,
      subject.field,
      message.field,
      honeypot,
      consentWrap,
      submit,
      status,
    );

    queueMicrotask(() => name.input.focus());
    return form;
  }

  // showConfirmation swaps the form for a success view in place.
  private showConfirmation(card: HTMLElement): void {
    const form = card.querySelector('.contact__form');
    form?.remove();
    const box = el('div', 'contact__success');
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.append(
      el('h2', 'contact__success-title', t('contact.success.title')),
      el('p', 'contact__success-lead', t('contact.success.lead')),
    );
    card.appendChild(box);
  }
}

type FieldOpts = {
  required?: boolean;
  autocomplete?: string;
  maxLength?: number;
  placeholder?: string;
};

function field(
  type: string,
  nameAttr: string,
  labelText: string,
  opts: FieldOpts = {},
): { field: HTMLElement; input: HTMLInputElement } {
  const wrap = el('div', 'contact__field');
  const label = el('label', 'contact__label', labelText);
  label.htmlFor = `contact-${nameAttr}`;
  const input = el('input', 'contact__input');
  input.id = `contact-${nameAttr}`;
  input.name = nameAttr;
  input.type = type;
  if (opts.required) input.required = true;
  if (opts.autocomplete) input.autocomplete = opts.autocomplete as AutoFill;
  if (opts.maxLength) input.maxLength = opts.maxLength;
  if (opts.placeholder) input.placeholder = opts.placeholder;
  wrap.append(label, input);
  return { field: wrap, input };
}

function textareaField(
  nameAttr: string,
  labelText: string,
  opts: FieldOpts = {},
): { field: HTMLElement; input: HTMLTextAreaElement } {
  const wrap = el('div', 'contact__field');
  const label = el('label', 'contact__label', labelText);
  label.htmlFor = `contact-${nameAttr}`;
  const input = el('textarea', 'contact__input contact__textarea');
  input.id = `contact-${nameAttr}`;
  input.name = nameAttr;
  input.rows = 6;
  if (opts.required) input.required = true;
  if (opts.maxLength) input.maxLength = opts.maxLength;
  wrap.append(label, input);
  return { field: wrap, input };
}
